import { Component, OnInit, OnDestroy, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SudokuService, SudokuGrid } from '../services/sudoku.service';
import { LanguageService } from '../services/language.service';
import { SeoService } from '../services/seo.service';
import { AdSenseBlockComponent } from '../shared/adsense-block/adsense-block.component';

@Component({
  selector: 'app-sudoku',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, AdSenseBlockComponent],
  template: `
    <div class="game-container">
      <div class="game-ad-container">
        <app-adsense-block [adSlot]="'9526827533'"></app-adsense-block>
      </div>
      <header class="game-header">
        <button class="btn-back" [routerLink]="['/']">
          <span class="material-symbols-outlined">arrow_back</span>
        </button>
        <h1>{{ t('sudoku_title') }}</h1>
        <div class="timer" [class.stopped]="gameWon">
          <span class="material-symbols-outlined">{{ gameWon ? 'check_circle' : 'timer' }}</span>
          {{ formatTime(seconds) }}
        </div>
      </header>

      <main class="game-content">
        <div class="controls glass-card">
          <!-- Settings Section (Hidden by default) -->
          <div class="settings-panel" [class.expanded]="showSettings">
            <div class="setting-group">
              <label>{{ t('grid_size') }}</label>
              <div class="difficulty-selector">
                <button (click)="startNewGame(4, difficulty)" [class.active]="size === 4">4x4</button>
                <button (click)="startNewGame(6, difficulty)" [class.active]="size === 6">6x6</button>
              </div>
            </div>

            <div class="setting-group">
              <label>{{ t('difficulty') }}</label>
              <select class="premium-select" [(ngModel)]="difficulty" (change)="startNewGame(size, difficulty)">
                <option value="easy">{{ t('easy') }}</option>
                <option value="medium">{{ t('medium') }}</option>
                <option value="hard">{{ t('hard') }}</option>
              </select>
            </div>

            <button class="btn-primary" (click)="startNewGame(size, difficulty)">{{ t('restart') }}</button>
            <button class="btn-secondary" (click)="showRules = !showRules">
              <span class="material-symbols-outlined">help</span>
              {{ t('how_to_play') }}
            </button>

            <!-- Mobile Only Footer Links (Inside Menu) -->
            <div class="mobile-footer-links">
              <a [routerLink]="['/privacy-policy']">{{ t('privacy_title') }}</a>
              <span class="separator">•</span>
              <a [routerLink]="['/contact']">{{ t('contact_title') }}</a>
            </div>
          </div>
          
          <!-- Gameplay Actions (Always visible) -->
          <div class="game-actions">
            <button class="action-btn" (click)="getHint()">
              <span class="material-symbols-outlined">lightbulb</span>
              {{ t('hint') }}
            </button>
            <button class="action-btn" [class.active]="isNotesMode" (click)="toggleNotes()">
              <span class="material-symbols-outlined">edit</span>
              {{ t('notes') }} {{ isNotesMode ? t('on') : t('off') }}
            </button>
            <!-- Mobile Menu Toggle -->
            <button class="action-btn mobile-menu-btn" (click)="showSettings = !showSettings" [class.active]="showSettings">
              <span class="material-symbols-outlined">{{ showSettings ? 'close' : 'settings' }}</span>
              Menu
            </button>
          </div>

          <!-- Hint Box with Slide Animation -->
          <div class="hint-box" [class.visible]="!!hintText">
            <p>{{ hintText }}</p>
          </div>
        </div>

        <div class="board-wrapper">
          <div class="sudoku-board-outer" [style.--size]="size">
            <div class="sudoku-board" [style.grid-template-columns]="'repeat(' + size + ', 1fr)'">
              <div *ngFor="let row of grid; let rIdx = index; trackBy: trackByIndex" class="sudoku-row">
                <div *ngFor="let cell of row; let cIdx = index; trackBy: trackByIndex" 
                     class="sudoku-cell"
                     [class.initial]="isInitial[rIdx][cIdx]"
                     [class.selected]="selectedCell?.r === rIdx && selectedCell?.c === cIdx"
                     [class.hinted]="hintedCell?.r === rIdx && hintedCell?.c === cIdx"
                     [class.thick-right]="isThickRight(cIdx)"
                     [class.thick-bottom]="isThickBottom(rIdx)"
                     [class.error]="isError(rIdx, cIdx)"
                     (click)="selectCell(rIdx, cIdx)">
                  {{ cell }}
                </div>
              </div>
            </div>
          </div>

          <div class="input-section" *ngIf="gameStarted">
            <div class="num-pad-grid">
              <button *ngFor="let num of numbers; trackBy: trackByIndex" (click)="inputNumber(num)" class="num-btn">{{ num }}</button>
              <button (click)="inputNumber(null)" class="tool-btn">
                <span class="material-symbols-outlined">close</span>
                <span>{{ t('erase') }}</span>
              </button>
              <button (click)="undo()" class="tool-btn" [disabled]="history.length === 0">
                <span class="material-symbols-outlined">undo</span>
                <span>{{ t('undo') }}</span>
              </button>
            </div>
          </div>
        </div>
      </main>



      <div class="rules-overlay" *ngIf="showRules" (click)="showRules = false">
        <div class="rules-modal" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <h3>{{ t('how_to_play') }}</h3>
            <button class="btn-close" (click)="showRules = false">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          
          <div class="rules-section">
            <h4>{{ t('sudoku_desc_seo') }}</h4>
            <p>{{ t('about_sudoku_title') }}</p>
          </div>

          <div class="rules-section">
            <h4>{{ t('sudoku_rules_seo_title') }}</h4>
            <ul>
              <li>1-{{size}} in each Row</li>
              <li>1-{{size}} in each Column</li>
              <li>1-{{size}} in each Box</li>
              <li><b>Diagonals!</b> The two main diagonals must also contain 1-{{size}} unique digits.</li>
            </ul>
          </div>

          <div class="rules-note">
             <p><strong>Note:</strong> {{ t('sudoku_strategy_title') }}</p>
          </div>
        </div>
      </div>

      <div class="win-modal" *ngIf="gameWon">
        <div class="modal-content glass-card">
          <span class="material-symbols-outlined icon-win">emoji_events</span>
          <h2>{{ t('congrats') }}</h2>
          <p>{{ t('sudoku_win_msg') }} {{ formatTime(seconds) }}</p>
          <button class="btn-restart" (click)="startNewGame(size)">{{ t('restart') }}</button>
        </div>
      </div>
      
       <footer class="game-footer">
        <a [routerLink]="['/privacy-policy']">{{ t('privacy_title') }}</a>
        <span class="separator">•</span>
        <a [routerLink]="['/contact']">{{ t('contact_title') }}</a>
      </footer>
    </div>
  `,
})
export class SudokuComponent implements OnInit, OnDestroy {
  size: 4 | 6 = 6;
  difficulty: 'easy' | 'medium' | 'hard' = 'medium';
  grid: SudokuGrid = [];
  solution: number[][] = [];
  isInitial: boolean[][] = [];
  selectedCell: { r: number, c: number } | null = null;
  hintedCell: { r: number, c: number } | null = null;
  hintText: string | null = null;

  isNotesMode = false;
  history: { r: number, c: number, oldVal: number | null, newVal: number | null }[] = [];
  errors: { row: number, col: number }[] = [];
  errorMatrix: boolean[][] = [];
  validateTimeout: any;

  seconds = 0;
  timerInterval: any;
  gameStarted = false;
  gameWon = false;
  _showRules = false;
  get showRules() { return this._showRules; }
  set showRules(v: boolean) {
    this._showRules = v;
    if (isPlatformBrowser(this.platformId)) {
      if (v) document.body.classList.add('modal-open');
      else document.body.classList.remove('modal-open');
    }
  }
  showSettings = false;



  constructor(
    private sudokuService: SudokuService,
    private langService: LanguageService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  t(key: string, params: any = {}) {
    return this.langService.translate(key, params);
  }

  ngOnInit() {
    this.langService.currentLang$.subscribe(lang => {
      if (lang === 'fr') {
        this.seoService.setPageData({
          title: 'Entraînement Sudoku - Inspiré des Jeux LinkedIn (Diagonal)',
          description: 'Pratiquez le Sudoku Minister (Diagonal) comme sur LinkedIn. Grilles gratuites pour s\'entraîner et progresser aux jeux de logique.',
          keywords: ['sudoku linkedin', 'entraînement sudoku', 'minister sudoku', 'sudoku diagonal', 'linkedin games', 'jeu de logique'],
          type: 'game'
        });
      } else {
        this.seoService.setPageData({
          title: 'Sudoku Training - Inspired by LinkedIn Games (Diagonal)',
          description: 'Train for LinkedIn-style Sudoku (Minister/Diagonal) assessments. Free unlimited puzzles to improve your logic skills. The best Sudoku training.',
          keywords: ['linkedin sudoku', 'minister sudoku', 'diagonal sudoku', 'sudoku training', 'logic puzzles', 'brain games'],
          type: 'game'
        });
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      this.startNewGame(6);
    }
  }


  ngOnDestroy() {
    this.stopTimer();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.gameStarted || !this.selectedCell) return;

    const key = event.key;
    if (key >= '1' && key <= this.size.toString()) {
      this.inputNumber(parseInt(key));
    } else if (key === 'Backspace' || key === 'Delete' || key === '0') {
      this.inputNumber(null);
    } else if (key === 'z' && (event.ctrlKey || event.metaKey)) {
      this.undo();
    } else if (key.startsWith('Arrow')) {
      this.navigateSelection(key);
    }
  }

  navigateSelection(direction: string) {
    if (!this.selectedCell) return;
    let { r, c } = this.selectedCell;
    if (direction === 'ArrowUp') r = (r - 1 + this.size) % this.size;
    else if (direction === 'ArrowDown') r = (r + 1) % this.size;
    else if (direction === 'ArrowLeft') c = (c - 1 + this.size) % this.size;
    else if (direction === 'ArrowRight') c = (c + 1) % this.size;

    this.selectedCell = { r, c };
    this.hintedCell = null;
  }

  get numbers() {
    return Array.from({ length: this.size }, (_, i) => i + 1);
  }

  startNewGame(size: 4 | 6, difficulty: 'easy' | 'medium' | 'hard' = 'medium') {
    this.size = size;
    this.difficulty = difficulty;
    const { puzzle, solution } = this.sudokuService.generatePuzzle(size, difficulty);
    this.grid = puzzle;
    this.solution = solution;
    this.isInitial = puzzle.map(row => row.map(cell => cell !== null));
    this.selectedCell = null;
    this.hintedCell = null;
    this.hintText = null;
    this.history = [];
    this.gameWon = false;
    this.gameStarted = true;
    this.resetTimer();
    this.startTimer();
    this.validate(true);
  }

  isThickRight(cIdx: number): boolean {
    if (this.size === 4) return cIdx === 1;
    if (this.size === 6) return cIdx === 2;
    return false;
  }

  isThickBottom(rIdx: number): boolean {
    if (this.size === 4) return rIdx === 1;
    if (this.size === 6) return rIdx === 1 || rIdx === 3;
    return false;
  }

  selectCell(r: number, c: number) {
    this.selectedCell = { r, c };
    this.hintedCell = null;
    this.hintText = null; /* Clear hint on interaction */
  }

  toggleNotes() {
    this.isNotesMode = !this.isNotesMode;
  }

  inputNumber(num: number | null) {
    if (!this.selectedCell || this.isInitial[this.selectedCell.r][this.selectedCell.c]) return;
    const { r, c } = this.selectedCell;
    const oldVal = this.grid[r][c];

    if (oldVal === num) return;

    this.history.push({ r, c, oldVal, newVal: num });
    this.grid[r][c] = num;
    this.validate(false);
  }

  undo() {
    const lastAction = this.history.pop();
    if (lastAction) {
      this.grid[lastAction.r][lastAction.c] = lastAction.oldVal;
      this.validate(true);
    }
  }

  validate(immediate: boolean = true) {
    if (this.validateTimeout) clearTimeout(this.validateTimeout);

    const { valid, errors } = this.sudokuService.validateGrid(this.grid, this.size);
    this.errors = errors;

    // 1. Handle disappearing errors immediately
    const actualErrorSet = new Set(errors.map(e => `${e.row}, ${e.col}`));
    for (let r = 0; r < this.size; r++) {
      if (!this.errorMatrix[r]) continue;
      for (let c = 0; c < this.size; c++) {
        if (this.errorMatrix[r][c] && !actualErrorSet.has(`${r}, ${c}`)) {
          this.errorMatrix[r][c] = false;
        }
      }
    }

    const finalizeValidation = () => {
      const fresh = this.sudokuService.validateGrid(this.grid, this.size);
      this.errors = fresh.errors;
      this.errorMatrix = Array.from({ length: this.size }, () => Array(this.size).fill(false));
      fresh.errors.forEach(e => this.errorMatrix[e.row][e.col] = true);

      // checkWin logic integrated here
      let complete = true;
      for (let r = 0; r < this.size; r++) {
        for (let c = 0; c < this.size; c++) {
          if (this.grid[r][c] === null) complete = false;
        }
      }

      if (complete && fresh.errors.length === 0) {
        this.gameWon = true;
        this.gameStarted = false;
        this.stopTimer();
      }
    };

    if (immediate || valid) {
      finalizeValidation();
    } else {
      const hasNewUnseenErrors = errors.some(e => !this.errorMatrix[e.row][e.col]);
      if (hasNewUnseenErrors) {
        this.validateTimeout = setTimeout(() => finalizeValidation(), 500);
      }
    }
  }

  isError(r: number, c: number): boolean {
    return this.errorMatrix[r] ? this.errorMatrix[r][c] : false;
  }

  getHint() {
    const hint = this.sudokuService.getHint(this.grid, this.solution, this.size);
    if (hint) {
      this.hintedCell = { r: hint.row, c: hint.col };
      this.hintText = hint.explanation;
      this.inputNumber(hint.value);
      this.validate(true);
    }
  }

  formatTime(totalSeconds: number): string {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}: ${secs.toString().padStart(2, '0')}`;
  }

  startTimer() {
    this.stopTimer();
    if (isPlatformBrowser(this.platformId)) {
      this.timerInterval = setInterval(() => {
        this.seconds++;
      }, 1000);
    }
  }

  stopTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  resetTimer() {
    this.seconds = 0;
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }
}
